import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  formatTableValue,
  PERIOD_COLUMNS,
  type TotalsByWeekData,
} from './storeCubeCartonUpdateMockData'

interface Props {
  data: TotalsByWeekData
}

const LABEL_COL_WIDTH = 180
const DATA_COL_WIDTH = 96

export function StoreCubeCartonUpdateTable({ data }: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const toggle = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
      <div className="max-h-[640px] overflow-auto">
        <table
          className="w-full border-separate text-xs"
          style={{ borderSpacing: 0 }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: LABEL_COL_WIDTH,
                  minWidth: LABEL_COL_WIDTH,
                  left: 0,
                }}
                className="sticky top-0 left-0 z-30 border-r border-b border-border bg-white px-3 py-2 text-left text-xs font-semibold text-foreground"
              >
                Totals by Week
              </th>
              {PERIOD_COLUMNS.map(period => (
                <th
                  key={period}
                  style={{ width: DATA_COL_WIDTH, minWidth: DATA_COL_WIDTH }}
                  className="sticky top-0 z-20 border-r border-b border-border bg-white px-3 py-2 text-right text-xs font-semibold text-foreground"
                >
                  {period}
                </th>
              ))}
              <th
                style={{ width: DATA_COL_WIDTH, minWidth: DATA_COL_WIDTH }}
                className="sticky top-0 z-20 border-b border-border bg-white px-3 py-2 text-right text-xs font-semibold text-foreground"
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {data.categoryRows.map(row => {
              const isExpanded = collapsed.has(row.category)
              return (
                <Fragment key={row.category}>
                  <tr className="bg-white hover:brightness-95">
                    <td
                      onClick={() => toggle(row.category)}
                      style={{
                        width: LABEL_COL_WIDTH,
                        minWidth: LABEL_COL_WIDTH,
                        left: 0,
                      }}
                      className="sticky left-0 z-10 cursor-pointer border-r border-b border-border bg-white px-3 py-1.5 text-left font-medium text-foreground"
                    >
                      <span className="inline-flex items-center gap-1">
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3 shrink-0" />
                        ) : (
                          <ChevronRight className="h-3 w-3 shrink-0" />
                        )}
                        {row.category}
                      </span>
                    </td>
                    {PERIOD_COLUMNS.map(period => (
                      <td
                        key={period}
                        style={{
                          width: DATA_COL_WIDTH,
                          minWidth: DATA_COL_WIDTH,
                        }}
                        className="border-r border-b border-border px-3 py-1.5 text-right tabular-nums"
                      >
                        {formatTableValue(row.values[period])}
                      </td>
                    ))}
                    <td
                      style={{
                        width: DATA_COL_WIDTH,
                        minWidth: DATA_COL_WIDTH,
                      }}
                      className="border-b border-border px-3 py-1.5 text-right font-medium tabular-nums"
                    >
                      {formatTableValue(row.total)}
                    </td>
                  </tr>
                  {isExpanded &&
                    row.dcRows.map(dcRow => (
                      <tr
                        key={`${row.category}-${dcRow.dc}`}
                        className="bg-white hover:bg-muted/20"
                      >
                        <td
                          style={{
                            width: LABEL_COL_WIDTH,
                            minWidth: LABEL_COL_WIDTH,
                            left: 0,
                          }}
                          className="sticky left-0 z-10 border-r border-b border-border bg-white px-3 py-1.5 pl-7 text-left text-muted-foreground"
                        >
                          {formatDistributionCenterName(dcRow.dc)}
                        </td>
                        {PERIOD_COLUMNS.map(period => (
                          <td
                            key={period}
                            style={{
                              width: DATA_COL_WIDTH,
                              minWidth: DATA_COL_WIDTH,
                            }}
                            className="border-r border-b border-border px-3 py-1.5 text-right tabular-nums text-muted-foreground"
                          >
                            {formatTableValue(dcRow.values[period])}
                          </td>
                        ))}
                        <td
                          style={{
                            width: DATA_COL_WIDTH,
                            minWidth: DATA_COL_WIDTH,
                          }}
                          className="border-b border-border px-3 py-1.5 text-right tabular-nums text-muted-foreground"
                        >
                          {formatTableValue(dcRow.total)}
                        </td>
                      </tr>
                    ))}
                </Fragment>
              )
            })}
            <tr className={cn('bg-slate-100 font-semibold')}>
              <td
                style={{
                  width: LABEL_COL_WIDTH,
                  minWidth: LABEL_COL_WIDTH,
                  left: 0,
                }}
                className="sticky left-0 z-10 border-r border-b border-border bg-slate-100 px-3 py-1.5 text-left text-foreground"
              >
                Total
              </td>
              {PERIOD_COLUMNS.map(period => (
                <td
                  key={period}
                  style={{ width: DATA_COL_WIDTH, minWidth: DATA_COL_WIDTH }}
                  className="border-r border-b border-border px-3 py-1.5 text-right tabular-nums"
                >
                  {formatTableValue(data.totalRow.values[period])}
                </td>
              ))}
              <td
                style={{ width: DATA_COL_WIDTH, minWidth: DATA_COL_WIDTH }}
                className="border-b border-border px-3 py-1.5 text-right tabular-nums"
              >
                {formatTableValue(data.totalRow.total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
